import { inject, Injectable } from "@angular/core";
import { NGXLogger } from "ngx-logger";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { UserService } from "./user.service";
import {
  ENVIRONMENT,
  Pageable,
  Project,
  User,
  Container,
  ContainerFile,
  UserImage,
  QuoteItem,
} from "@company-box/models";

@Injectable({
  providedIn: "root",
})
export class ProjectService {
  userService: UserService = inject(UserService);
  protected httpClient: HttpClient = inject(HttpClient);
  protected logger: NGXLogger = inject(NGXLogger);
  protected environment: any = inject(ENVIRONMENT);

  constructor() {}

  getBasicDesigns() {
    return new Promise((resolve, reject) => {
      this.userService.getCurrentUser().then(
        (user: { id: any }) => {
          this.httpClient
            .get(`${this.environment.apiHost}/project/design/${user.id}/list?designType=basic&theme=`)
            .subscribe(
              (response: any) => {
                resolve(response);
              },
              (error) => {
                reject(error || error.message);
              }
            );
        },
        (error: { message: any }) => {
          this.logger.debug(error.message || error);
          reject(false);
        }
      );
    });
  }
  createOrderItem(design: any) {
    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return new Promise<any>((resolve, reject) => {
      const payload = JSON.stringify(design);
      this.httpClient.post(`${this.environment.apiHost}/project/design/order/?clone=false`, payload, headers).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getDownloadLink(container: string, fileset: string): string {
    return `${this.environment.apiHost}/project/download/${container}/${fileset}`;
  }

  listProjects(
    userId: number,
    page: number,
    options: ProjectSearchOptions = {
      includeUser: false,
      includeType: false,
      createdBy: "ALL",
    }
  ): Promise<Pageable<Project>> {
    // this.logger.debug('Loading projects');
    let url = `${this.environment.apiHost}/project/design/`;
    if (userId !== 0) {
      url += userId;
    } else {
      url += "_";
    }
    url += "/list";
    if (userId !== 0) {
      url += `/page`;
    }
    url += `?includeScreenshot=false&includeContent=false&`;

    if (page > 1) {
      const offset = page - 1;
      url += `offset=${offset}&`;
    }
    if (options.pageSize) {
      url += `limit=${options.pageSize}&`;
    }
    if (options.includeUser) {
      url += `includeUser=${options.includeUser}&`;
    }
    if (options.includeType) {
      url += `includeType=${options.includeType}&`;
    }
    if (options.createdBy) {
      url += `createdBy=${options.createdBy}&`;
    }
    if (options.subcategory) {
      url += `subcategory=${options.subcategory}&`;
    }
    if (options.designType) {
      url += `designType=${options.designType}&`;
    }
    if (options.preset) {
      url += `preset=${options.preset}&`;
    }

    url += `sort=-createdTime`;
    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          const list: Project[] = [];
          (response.content as any[]).forEach((object) => {
            const project: Project = new Project().deserialize(object);
            if (!project.user) {
              if (project.userId && project.userId !== 0) {
                this.userService.getUser(project.userId).then((res) => {
                  project.user = new User().deserialize(res);
                });
              } else {
                project.user = User.EMPTY;
              }
            }
            list.push(project);
          });
          const newPage: Pageable<Project> = new Pageable<Project>().deserialize(response);
          newPage.content = list;
          this.logger.debug("Loaded projects", newPage);
          resolve(newPage);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getProjectById(
    projectId: number,
    includeUser: boolean,
    includeType: boolean,
    includeScreenshot: boolean
  ): Promise<Project> {
    // this.logger.debug('Loading project ' + projectId + ' by user ' + userId);
    let url = `${this.environment.apiHost}/project/design/_/${projectId}?includeUser=${includeUser}&includeType=${includeType}`;
    if (includeScreenshot) {
      url += `&includeScreenshot=${includeScreenshot}`;
    }
    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          const project: Project = new Project().deserialize(response);
          this.logger.debug("Loaded project", project);
          resolve(project);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getProjectByContainer(userId: string, containerName: string, includeUser: boolean, includeType: boolean, includeScreenshot = false): Promise<Project> {
    return new Promise((resolve, reject) => {
        const url = `${this.environment.apiHost}/project/design/${userId}/container/${containerName}?includeUser=${includeUser}&includeType=${includeType}&includeScreenshot=${includeScreenshot}`;
        return this.httpClient.get(url).subscribe(
          (response: any) => {
            const project: Project = new Project().deserialize(response);
            this.logger.debug("Loaded project", project);
            resolve(project);
          },
          (error) => {
            this.logger.debug(error.message || error);
            reject(error.message || error);
          }
        )
    })
  }

  getContainerFiles(container: string): Promise<Container> {
    return new Promise((resolve, reject) => {
      // this.logger.debug('Loading container ' + container);
      const url = `${this.environment.apiHost}/project/container/${container}/files?includeContent=false`;
      this.httpClient.get(url).subscribe(
        (response: any) => {
          resolve(new Container().deserialize(response));
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getContainerFileDescriptor(container: string, filename: string): Promise<ContainerFile> {
    return new Promise((resolve, reject) => {
      // this.logger.debug('Loading container ' + container);
      const url = `${this.environment.apiHost}/project/container/${container}/files/content?filename=${filename}`;
      this.httpClient.get(url).subscribe(
        (response: any) => {
          resolve(new ContainerFile().deserialize(response.file));
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getContainerFileUrl(container: string, filename: string) {
    return `${this.environment.apiHost}/project/container/${container}/${filename}`;
  }

  getFileUrl(partial: string) {
    return `${this.environment.apiHost}/project/${partial}`;
  }

  getImages(userId: number): Promise<UserImage[]> {
    const url = `${this.environment.apiHost}/project/image/${userId}/list`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res: any) => {
          const images: UserImage[] = [];
          res.forEach((value: any) => {
            const userImage: UserImage = new UserImage().deserialize(value);
            this.logger.debug("Loaded user images", userImage);
            images.push(userImage);
          });
          resolve(images);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getImage(userId: number, name: string): Promise<UserImage> {
    const url = `${this.environment.apiHost}/project/image/${userId}/${name}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res: any) => {
          const userImage: UserImage = new UserImage().deserialize(res);
          this.logger.debug("Loaded user images", userImage);
          resolve(userImage);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getImageWithContent(userId: number, name: string): Promise<UserImage> {
    const url = `${this.environment.apiHost}/project/image/${userId}/${name}/dataUrl`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res: any) => {
          const userImage: UserImage = new UserImage().deserialize(res);
          this.logger.debug("Loaded user images", userImage);
          resolve(userImage);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getDesignUploadUrl(container: string, filename: string, convert: boolean = true): string {
    return `${this.environment.apiHost}/zuul/project/container/${container}/upload/design?filename=${filename}&convert=${convert}`;
  }

  getProofUploadUrl(container: string): string {
    return `${this.environment.apiHost}/zuul/project/container/${container}/proof`;
  }

  getTemplate(quoteItem: QuoteItem): Promise<any> {
    const url =
      `${this.environment.apiHost}/project/container/?code=${quoteItem.type?.code}&boxType=${quoteItem.type?.aeCode}&` +
      `flute=${quoteItem.boardType?.code}&boxLength=${quoteItem.length}&boxWidth=${quoteItem.width}&boxDepth=${quoteItem.depth}&`;

    return new Promise((resolve, reject) => {
      this.httpClient.post(url, {}).subscribe(
        (response: any) => {
          const result = {
            template: response.template,
            container: response.container,
          };
          this.logger.debug("Created template for quote item", quoteItem, result);
          resolve(result);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  createContainer(quoteItem: QuoteItem): Promise<string> {
    return new Promise((resolve, reject) => {
      this.userService.getCurrentUser().then(
        (user) => {
          this.getTemplate(quoteItem).then(
            (templateAndContainer) => {
              const result = templateAndContainer.container;
              this.logger.debug("Created container for quote item", quoteItem, result);
              resolve(result);
            },
            (reason) => {
              this.logger.debug(reason);
              reject(reason);
            }
          );
        },
        (reason) => {
          this.logger.debug(reason);
          reject(reason);
        }
      );
    });
  }

  updateProofing(project: Project): Promise<Project> {
    const url = `${this.environment.apiHost}/project/design/${project.id}/proofing`;
    return new Promise<Project>((resolve, reject) => {
      this.httpClient
        .put(url, {
          workableId: project.workableId,
          created: project.proofCreated,
          approved: project.proofApproved,
          url: project.proofUrl,
        })
        .subscribe(
          (response: any) => {
            const updatedProject: Project = new Project().deserialize(response);
            this.logger.debug("Updated proofing for project", project);
            resolve(updatedProject);
          },
          (error) => {
            this.logger.debug(error);
            reject(error);
          }
        );
    });
  }
}

export interface ProjectSearchOptions {
  includeUser?: boolean;
  includeType?: boolean;
  createdBy?: string;
  pageSize?: number | 10;
  subcategory?: string;
  designType?: string;
  preset?: string;
}
